import type { UnitSystem } from '@awwyeah/shared';

/**
 * Recipe detail page units toggle. `'original'` renders ingredients exactly as stored (the
 * default — non-destructive); `'us'`/`'metric'` convert for display only. Persisted per
 * device (localStorage), independent of the scale factor which is deliberately per-visit
 * (see `RecipeDetailPage`).
 */
export type UnitsPreference = 'original' | 'us' | 'metric';

export const UNITS_STORAGE_KEY = 'awwyeah:recipes:units';

/** Minimal `Storage` shape we depend on, so tests can pass a fake instead of real localStorage
 * (mirrors `apps/web/src/theme/theme.ts`). */
export type UnitsStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function isUnitsPreference(value: string | null | undefined): value is UnitsPreference {
  return value === 'original' || value === 'us' || value === 'metric';
}

/** Reads the persisted preference, falling back to `'original'` for anything missing/invalid. */
export function readStoredUnitsPreference(storage: UnitsStorage): UnitsPreference {
  let raw: string | null = null;
  try {
    raw = storage.getItem(UNITS_STORAGE_KEY);
  } catch {
    // Storage can throw (private browsing, quota) — fall back silently.
    return 'original';
  }
  return isUnitsPreference(raw) ? raw : 'original';
}

export function writeStoredUnitsPreference(
  storage: UnitsStorage,
  preference: UnitsPreference,
): void {
  try {
    storage.setItem(UNITS_STORAGE_KEY, preference);
  } catch {
    // Best-effort persistence; a failed write just means it reverts to 'original' next launch.
  }
}

/** `null` means "don't convert" — the `scaleIngredientForDisplay` sentinel for "as written". */
export function unitsPreferenceToSystem(preference: UnitsPreference): UnitSystem | null {
  return preference === 'original' ? null : preference;
}
